import { Injectable } from '@angular/core';
import { Store, Action } from '@ngrx/store';
import { Actions, Effect, ofType } from '@ngrx/effects';

import { Observable } from 'rxjs/Observable';
import 'rxjs/add/observable/fromPromise';
import { map, concatMap, withLatestFrom } from 'rxjs/operators';

import { BackendEndpointService } from '../../services/backend-endpoint.service';

import * as fromReducers from '../reducers';
import * as rootActions from '../actions/ui.actions';
import { uiSelectors } from '../selectors';

export type Action = rootActions.All;

@Injectable()
export class UiEffects {
  // -----------------------------------------------------------------------------------------
  // Touchpoint taxonomy effects

  @Effect()
  getTouchpointPortion$: Observable<Action> = this.actions$.pipe(
    ofType<rootActions.GetTouchpointPortion>(rootActions.GET_TOUCHPOINT_PORTION),
    map(action => action.payload),
    concatMap(payload => {
      // create the request
      const url = `touchpoints/portion`;
      const additionalHeaders = {
        params: {
          portions: payload.portions,
          options: JSON.stringify(payload.options)
        }
      };

      return Observable.fromPromise(this.backend.get(url, additionalHeaders)).pipe(
        map(res => {
          return new rootActions.SetTouchpointPortion({
            portions: res
          });
        })
      );
    })
  );

  @Effect()
  saveTouchpointPortion$: Observable<Action> = this.actions$.pipe(
    ofType<rootActions.SaveTouchpointPortion>(rootActions.SAVE_TOUCHPOINT_PORTION),
    map(action => action.payload),
    withLatestFrom(this.store$.select(uiSelectors.getTouchpoint)),
    concatMap(([payload, touchpointInStore]) => {
      const url = `touchpoints/${payload.id}/portion`;
      const body = {
        portions: { ...payload.portions, ...{ modifiedAt: touchpointInStore.modifiedAt } },
        options: payload.options
      };

      return Observable.fromPromise(this.backend.post(url, body)).pipe(
        map(res => {
          return new rootActions.SetTouchpointPortion({
            portions: res
          });
        })
      );
    })
  );

  // -----------------------------------------------------------------------------------------

  // -----------------------------------------------------------------------------------------
  // Goal taxonomy effects

  @Effect()
  getGoalPortion$: Observable<Action> = this.actions$.pipe(
    ofType<rootActions.GetGoalPortion>(rootActions.GET_GOAL_PORTION),
    map(action => action.payload),
    concatMap(payload => {
      // create the request
      const url = `goals/portion`;
      const additionalHeaders = {
        params: {
          portions: payload.portions,
          options: JSON.stringify(payload.options)
        }
      };

      return Observable.fromPromise(this.backend.get(url, additionalHeaders)).pipe(
        map(res => {
          return new rootActions.SetGoalPortion({
            portions: res
          });
        })
      );
    })
  );

  @Effect()
  saveGoalPortion$: Observable<Action> = this.actions$.pipe(
    ofType<rootActions.SaveGoalPortion>(rootActions.SAVE_GOAL_PORTION),
    map(action => action.payload),
    withLatestFrom(this.store$.select(uiSelectors.getGoal)),
    concatMap(([payload, goalInStore]) => {
      const url = `goals/${payload.id}/portion`;
      const body = {
        portions: { ...payload.portions, ...{ modifiedAt: goalInStore.modifiedAt } },
        options: payload.options
      };

      return Observable.fromPromise(this.backend.post(url, body)).pipe(
        map(res => {
          return new rootActions.SetGoalPortion({
            portions: res
          });
        })
      );
    })
  );

  // -----------------------------------------------------------------------------------------

  // -----------------------------------------------------------------------------------------
  // Trigger taxonomy effects

  @Effect()
  getTriggerPortion$: Observable<Action> = this.actions$.pipe(
    ofType<rootActions.GetTriggerPortion>(rootActions.GET_TRIGGER_PORTION),
    map(action => action.payload),
    concatMap(payload => {
      // create the request
      const url = `triggers/portion`;
      const additionalHeaders = {
        params: {
          portions: payload.portions,
          options: JSON.stringify(payload.options)
        }
      };

      return Observable.fromPromise(this.backend.get(url, additionalHeaders)).pipe(
        map(res => {
          return new rootActions.SetTriggerPortion({
            portions: res
          });
        })
      );
    })
  );

  @Effect()
  saveTriggerPortion$: Observable<Action> = this.actions$.pipe(
    ofType<rootActions.SaveTriggerPortion>(rootActions.SAVE_TRIGGER_PORTION),
    map(action => action.payload),
    withLatestFrom(this.store$.select(uiSelectors.getTrigger)),
    concatMap(([payload, triggerInStore]) => {
      const url = `triggers/${payload.id}/portion`;
      const body = {
        portions: { ...payload.portions, ...{ modifiedAt: triggerInStore.modifiedAt } },
        options: payload.options
      };

      return Observable.fromPromise(this.backend.post(url, body)).pipe(
        map(res => {
          return new rootActions.SetTriggerPortion({
            portions: res
          });
        })
      );
    })
  );

  // -----------------------------------------------------------------------------------------

  // -----------------------------------------------------------------------------------------
  // Languages effects

  @Effect()
  getLanguagesPortion$: Observable<Action> = this.actions$.pipe(
    ofType<rootActions.GetLanguagesPortion>(rootActions.GET_LANGUAGES_PORTION),
    map(action => action.payload),
    concatMap(payload => {
      // create the request
      const url = `languages/all/portion`;
      const additionalHeaders = {
        params: {
          portions: payload.portions
        }
      };

      return Observable.fromPromise(this.backend.get(url, additionalHeaders)).pipe(
        map(res => {
          return new rootActions.SetLanguagesPortion({
            portions: res
          });
        })
      );
    })
  );

  // -----------------------------------------------------------------------------------------

  // -----------------------------------------------------------------------------------------
  // Countries effects

  @Effect()
  getCountriesPortion$: Observable<Action> = this.actions$.pipe(
    ofType<rootActions.GetCountriesPortion>(rootActions.GET_COUNTRIES_PORTION),
    map(action => action.payload),
    concatMap(payload => {
      // create the request
      const url = `countries/all/portion`;
      const additionalHeaders = {
        params: {
          portions: payload.portions
        }
      };

      return Observable.fromPromise(this.backend.get(url, additionalHeaders)).pipe(
        map(res => {
          return new rootActions.SetCountriesPortion({
            portions: res
          });
        })
      );
    })
  );

  // -----------------------------------------------------------------------------------------

  constructor(
    private actions$: Actions,
    private store$: Store<fromReducers.IRootState>,
    private backend: BackendEndpointService
  ) {}
}



// WEBPACK FOOTER //
// ./src/app/shared/store/effects/ui.effects.ts